import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CheckCircle2, ChevronRight, Loader2, Search } from "lucide-react";
import { api } from "../../lib/api";
import { Input } from "../ui/input";
import { RecipeEditor } from "./RecipeEditor";
import { RecipeDraftsBar } from "./RecipeDraftsBar";
import { BASIS_LABEL } from "./kitchenUtils";

export const RecipesTab = ({ units }) => {
  const [items, setItems] = useState(null);
  const [recipes, setRecipes] = useState({});
  const [ings, setIngs] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(null);
  const [params] = useSearchParams();
  const deepItem = params.get("item");

  const load = () =>
    Promise.all([api.get("/admin/menu"), api.get("/admin/kitchen/recipes"), api.get("/admin/kitchen/ingredients")]).then(([m, r, i]) => {
      const list = m.data.items || m.data;
      setItems(list);
      setRecipes(r.data.recipes);
      setIngs(i.data.ingredients);
      // Deep link from the menu item editor: open that item's recipe.
      const hit = deepItem && list.find((x) => x.id === deepItem);
      if (hit) setOpen(hit);
    });
  useEffect(() => { load(); }, [deepItem]); // eslint-disable-line react-hooks/exhaustive-deps

  const shown = useMemo(() => {
    const s = q.trim().toLowerCase();
    return (items || []).filter((it) => !s || `${it.name} ${it.category}`.toLowerCase().includes(s));
  }, [items, q]);

  if (!items) return <Loader2 className="h-5 w-5 animate-spin text-brand-teal" />;
  const done = Object.values(recipes).filter((r) => r.line_count > 0 || (r.component_count > 0 && !r.missing_amounts)).length;
  return (
    <div className="space-y-3" data-testid="recipes-tab">
      <RecipeDraftsBar onDone={() => api.get("/admin/kitchen/recipes").then((r) => setRecipes(r.data.recipes))} />
      <p className="text-[12.5px] text-brand-charcoal/60" data-testid="recipes-progress">{done} of {items.length} menu items have a recipe.</p>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-charcoal/40" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find a menu item" data-testid="recipes-search" className="pl-9 h-11 rounded-full bg-white border-black/10" />
      </div>
      {open && (
        <RecipeEditor
          item={open}
          ings={ings}
          units={units}
          onClose={() => setOpen(null)}
          onSaved={(id, body) => { setRecipes((r) => ({ ...r, [id]: { basis: body.basis, line_count: body.lines.length, component_count: body.components.length, missing_amounts: body.components.filter((c) => c.amount == null).length } })); setOpen(null); }}
        />
      )}
      <div className="grid gap-2">
        {shown.map((it) => {
          const rec = recipes[it.id];
          return (
            <button key={it.id} type="button" onClick={() => setOpen(it)} data-testid={`recipe-item-${it.id}`} className="flex items-center gap-3 rounded-xl bg-white border border-black/10 px-4 py-2.5 text-left">
              <div className="min-w-0 flex-1">
                <p className="font-bold text-[14px] text-brand-charcoal truncate">{it.name}</p>
                <p className="text-[11.5px] text-brand-charcoal/55">{it.category}</p>
              </div>
              {rec?.line_count ? (
                <span className="inline-flex items-center gap-1 text-[11px] font-bold text-brand-teal" data-testid={`recipe-status-${it.id}`}>
                  <CheckCircle2 className="h-3.5 w-3.5" /> {rec.line_count} ingr. · {BASIS_LABEL[rec.basis]}
                </span>
              ) : rec?.component_count ? (
                <span className={`text-[11px] font-bold ${rec.missing_amounts ? "text-brand-gold-dark" : "text-brand-teal"}`} data-testid={`recipe-status-${it.id}`}>
                  {rec.component_count} checklist{rec.missing_amounts ? ` · ${rec.missing_amounts} need amounts` : ""}
                </span>
              ) : (
                <span className="text-[11px] font-bold text-brand-charcoal/40" data-testid={`recipe-status-${it.id}`}>No recipe</span>
              )}
              <ChevronRight className="h-4 w-4 text-brand-charcoal/40" />
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default RecipesTab;
